import type { Environment } from "@anota/shared";
import { LIMITS } from "@anota/shared/limits";

/** "Chrome 141", "Safari 18", "Edge 141"… (lo justo para reproducir un problema). */
export function browserName(ua: string): string | undefined {
  const rules: [RegExp, string][] = [
    [/Edg(?:e|A|iOS)?\/(\d+)/, "Edge"],
    [/OPR\/(\d+)/, "Opera"],
    [/SamsungBrowser\/(\d+)/, "Samsung Internet"],
    [/Firefox\/(\d+)/, "Firefox"],
    [/FxiOS\/(\d+)/, "Firefox"],
    [/CriOS\/(\d+)/, "Chrome"],
    [/Chrome\/(\d+)/, "Chrome"],
    [/Version\/(\d+)[\d.]* (?:Mobile\/\S+ )?Safari/, "Safari"],
  ];
  for (const [re, name] of rules) {
    const m = re.exec(ua);
    if (m) return `${name} ${m[1]}`;
  }
  return undefined;
}

export function osName(ua: string): string | undefined {
  if (/Windows NT/.test(ua)) return "Windows";
  if (/iPhone|iPad|iPod/.test(ua)) return "iOS";
  if (/Android/.test(ua)) return "Android";
  if (/CrOS/.test(ua)) return "ChromeOS";
  if (/Mac OS X|Macintosh/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return undefined;
}

export function environment(): Environment {
  const ua = navigator.userAgent;
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  const browser = browserName(ua);
  const os = osName(ua);
  return {
    url: safeUrl(location.href).slice(0, LIMITS.url),
    ...(document.title ? { title: document.title.slice(0, 300) } : {}),
    userAgent: ua.slice(0, 500),
    ...(browser ? { browser } : {}),
    ...(os ? { os } : {}),
    language: navigator.language.slice(0, 35),
    ...(timezone ? { timezone } : {}),
    viewport: { width: innerWidth, height: innerHeight },
    screen: { width: screen.width, height: screen.height },
    devicePixelRatio: Math.round(devicePixelRatio * 100) / 100,
    scroll: { x: Math.round(scrollX), y: Math.round(scrollY) },
  };
}

/** Parámetros que suelen llevar secretos o datos personales (enlaces de restablecer, OAuth, emails…). */
const SECRET_PARAM = /^(.*token.*|code|state|key|api[-_]?key|secret|password|pass|pwd|signature|sig|session|auth|otp|email|e-?mail|reset)$/i;

/**
 * La dirección de la página para la nota: la ruta y la query (sirven para saber qué se veía), pero con el valor de
 * los parámetros sensibles tapado y sin un fragmento que lleve datos (#access_token=…). Lo demás, tal cual.
 */
export function safeUrl(href: string): string {
  try {
    const u = new URL(href);
    for (const [k] of [...u.searchParams]) if (SECRET_PARAM.test(k)) u.searchParams.set(k, "…");
    if (/[=&]/.test(u.hash)) u.hash = "";
    return u.toString();
  } catch {
    return href;
  }
}
